import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { normalizeDecimals } from './normalizeDecimals';
import { getErrorMessage } from '@/lib/errorMessages';
import { translate } from '@/lib/i18n';
import { IMPERSONATION_EXIT_PATH } from '@/lib/constants';
import type { ApiErrorBody, LoginResponse } from '@/types/api';

/**
 * TZ-docs/07-api.md §7.1 — axios instance, JWT interceptor, refresh rotatsiyasi,
 * xato normalizatsiyasi (TZ-docs/19 kodlarini o'zbekcha matnga aylantirish).
 */

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
  timeout: 15_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/** Normalizatsiya qilingan xatolik — komponentlar shu shaklda ushlaydi */
export class ApiError extends Error {
  code: string;
  details?: ApiErrorBody['error']['details'];

  constructor(code: string, message: string, details?: ApiErrorBody['error']['details']) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, user, impersonation, stopImpersonation } = useAuthStore.getState();

  // Impersonation sessiyasida refresh token YO'Q (qasddan — backend bermaydi).
  // Token muddati tugagach admin o'z sessiyasiga avtomatik qaytariladi, aks
  // holda ilova 401 tsiklida qotib qolardi.
  if (impersonation) {
    stopImpersonation();
    // SPA ichida qolsak joriy (masalan, menejer) sahifaning `RoleRoute`i
    // adminni `/403` ga uloqtiradi va begona kesh ekranda qoladi. Shuning
    // uchun keshni tozalab, admin sahifasiga to'liq qayta yuklaymiz.
    // Dinamik import — `identityCache` → `queryClient` → `client` aylanasini oldini oladi.
    const { resetIdentityCaches } = await import('@/lib/session/identityCache');
    await resetIdentityCaches();
    window.location.assign(IMPERSONATION_EXIT_PATH);
    return null;
  }

  if (!refreshToken || !user) return null;

  try {
    const res = await axios.post<{ data: LoginResponse }>(
      `${apiClient.defaults.baseURL}/auth/refresh`,
      { refresh_token: refreshToken },
    );
    const { access_token, refresh_token, user: refreshedUser } = res.data.data;
    useAuthStore.getState().setSession({ accessToken: access_token, refreshToken: refresh_token, user: refreshedUser });
    return access_token;
  } catch {
    useAuthStore.getState().logout();
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => {
    // Pul maydonlari backend'dan satr sifatida keladi ("252000.00") — songa
    // aylantiramiz, aks holda `sum + qiymat` satr birikmasiga aylanadi.
    // Fayl (Blob/ArrayBuffer) javoblariga tegilmaydi.
    if (response.data && typeof response.data === 'object' && !(response.data instanceof Blob)) {
      response.data = normalizeDecimals(response.data);
    }
    return response;
  },
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // 401 TOKEN_EXPIRED bo'lsa — bitta marta refresh qilib qayta urinamiz
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      refreshPromise ??= refreshAccessToken();
      const newToken = await refreshPromise;
      refreshPromise = null;
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return apiClient(original);
      }
    }

    if (!error.response) {
      return Promise.reject(new ApiError('NETWORK_ERROR', getErrorMessage('NETWORK_ERROR')));
    }

    const body = error.response.data;
    const code = body?.error?.code ?? 'INTERNAL_ERROR';
    // FastAPI `HTTPException` standart `{"detail": "..."}` shaklida qaytaradi
    // (masalan `/telegram/*` endpointlari). Ilgari faqat `body.error.message`
    // o'qilardi, shuning uchun aniq sabab yo'qolib, foydalanuvchi umumiy
    // "Tizimda xatolik" xabarini ko'rardi.
    const detail = typeof body?.detail === 'string' ? body.detail : undefined;
    // Backend matni o'zbekcha keladi; lug'atda bo'lsa rus tiliga o'giriladi.
    const serverMessage = body?.error?.message ?? detail;
    const message = serverMessage ? translate(serverMessage) : getErrorMessage(code);
    return Promise.reject(new ApiError(code, message, body?.error?.details));
  },
);
